import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Plus, Users, Sparkles, Edit2, Trash2, ToggleLeft, ToggleRight, Loader2, AlertCircle, Lock, RefreshCw, RotateCcw, Ban, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { PERMANENT_ACHIEVEMENTS, CYCLE_MILESTONES, AchievementDefinition } from './achievementDefinitions';
import { useCustomAchievements, CustomAchievement } from '@/hooks/useCustomAchievements';
import { useSystemAchievementOverrides, MergedAchievement } from '@/hooks/useSystemAchievementOverrides';
import { AchievementForm } from './AchievementForm';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const CATEGORY_INFO: Record<string, { icon: string; name: string; color: string }> = {
  ventas: { icon: '🛒', name: 'Ventas', color: 'from-blue-500/20 to-cyan-500/20' },
  canjes: { icon: '🎁', name: 'Canjes', color: 'from-pink-500/20 to-purple-500/20' },
  ahorros: { icon: '💰', name: 'Ahorros', color: 'from-green-500/20 to-emerald-500/20' },
  entrenamiento: { icon: '🎓', name: 'Entrenamiento', color: 'from-indigo-500/20 to-blue-500/20' },
  ranking: { icon: '🏆', name: 'Ranking', color: 'from-amber-500/20 to-yellow-500/20' },
  especial: { icon: '✨', name: 'Especiales', color: 'from-purple-500/20 to-pink-500/20' },
};

interface SystemStats {
  totalSellersWithAchievements: number;
  totalAchievementsAwarded: number;
}

export const AdminAchievementsManager: React.FC = () => {
  const {
    customAchievements,
    loading: customLoading,
    createAchievement,
    updateAchievement,
    deleteAchievement,
    toggleActive,
    refetch,
  } = useCustomAchievements();

  const {
    getMergedPermanentAchievements,
    getMergedCycleMilestones,
    getAchievementPoints,
    updateOverride,
    deleteOverride,
    loading: overridesLoading,
    refetch: refetchOverrides,
  } = useSystemAchievementOverrides();

  const [systemStats, setSystemStats] = useState<SystemStats>({ totalSellersWithAchievements: 0, totalAchievementsAwarded: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<CustomAchievement | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // System achievement editing
  const [editingSystemAchievement, setEditingSystemAchievement] = useState<MergedAchievement | null>(null);
  const [systemEditForm, setSystemEditForm] = useState({
    badge: '',
    name: '',
    description: '',
    requirement: '',
    points: 0,
    is_active: true,
  });
  const [savingSystemEdit, setSavingSystemEdit] = useState(false);
  const [restoringDefault, setRestoringDefault] = useState<string | null>(null);

  const mergedPermanent = getMergedPermanentAchievements();
  const mergedCycle = getMergedCycleMilestones();
  const allMergedAchievements = [...mergedPermanent, ...mergedCycle];
  
  const activeCustomCount = customAchievements.filter(a => a.is_active).length;
  const activeSystemCount = allMergedAchievements.filter(a => a.isActive).length;
  const totalAchievementsCount = activeSystemCount + activeCustomCount;
  
  const totalSystemPoints = allMergedAchievements.filter(a => a.isActive).reduce((sum, a) => sum + a.points, 0);
  const totalCustomPoints = customAchievements.filter(a => a.is_active).reduce((sum, a) => sum + a.points, 0);
  const totalPoints = totalSystemPoints + totalCustomPoints;

  useEffect(() => {
    fetchSystemStats();
  }, []);

  const fetchSystemStats = async () => {
    try {
      setStatsLoading(true);
      const { data, error } = await supabase
        .from('seller_achievements')
        .select('seller_id');

      if (error) throw error;

      const uniqueSellers = new Set((data || []).map(a => a.seller_id));
      setSystemStats({
        totalSellersWithAchievements: uniqueSellers.size,
        totalAchievementsAwarded: (data || []).length,
      });
    } catch (err) {
      console.error('Error fetching system stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleEditSystemAchievement = (achievement: MergedAchievement) => {
    setEditingSystemAchievement(achievement);
    setSystemEditForm({
      badge: achievement.badge,
      name: achievement.name,
      description: achievement.description,
      requirement: achievement.requirement,
      points: achievement.points,
      is_active: achievement.isActive,
    });
  };

  const handleSaveSystemAchievement = async () => {
    if (!editingSystemAchievement) return;
    
    setSavingSystemEdit(true);
    try {
      const success = await updateOverride(editingSystemAchievement.type, {
        badge: systemEditForm.badge,
        name: systemEditForm.name,
        description: systemEditForm.description,
        requirement: systemEditForm.requirement,
        points: systemEditForm.points,
        is_active: systemEditForm.is_active,
      });
      
      if (success) {
        setEditingSystemAchievement(null);
      }
    } finally {
      setSavingSystemEdit(false);
    }
  };

  const handleRestoreDefault = async (type: string) => {
    setRestoringDefault(type);
    try {
      await deleteOverride(type);
    } finally {
      setRestoringDefault(null);
    }
  };

  const handleDeactivateSystemAchievement = async () => {
    if (!editingSystemAchievement) return;
    setSavingSystemEdit(true);
    try {
      const success = await updateOverride(editingSystemAchievement.type, { 
        is_active: false 
      });
      if (success) {
        setEditingSystemAchievement(null);
        toast.success('Logro desactivado. Los puntos se han recalculado.');
      }
    } finally {
      setSavingSystemEdit(false);
    }
  };

  const handleActivateSystemAchievement = async () => {
    if (!editingSystemAchievement) return;
    setSavingSystemEdit(true);
    try {
      const success = await updateOverride(editingSystemAchievement.type, { 
        is_active: true 
      });
      if (success) {
        setEditingSystemAchievement(null);
        toast.success('Logro activado. Los puntos se han recalculado.');
      }
    } finally {
      setSavingSystemEdit(false);
    }
  };

  const handleEdit = (achievement: CustomAchievement) => {
    setEditingAchievement(achievement);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    const result = await deleteAchievement(deletingId);
    if (result.success) {
      toast.success('Logro eliminado');
    } else {
      toast.error(result.error || 'Error al eliminar');
    }
    setDeletingId(null);
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    const result = await toggleActive(id, !currentActive);
    if (result.success) {
      toast.success(currentActive ? 'Logro desactivado' : 'Logro activado');
    } else {
      toast.error(result.error || 'Error al cambiar estado');
    }
  };

  const handleFormSubmit = async (data: any) => {
    if (editingAchievement) {
      return updateAchievement(editingAchievement.id, data);
    }
    return createAchievement(data);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAchievement(null);
  };

  const groupedSystemAchievements = Object.keys(CATEGORY_INFO).reduce((acc, category) => {
    acc[category] = allMergedAchievements.filter(a => a.category === category);
    return acc;
  }, {} as Record<string, MergedAchievement[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 border border-purple-500/20 rounded-2xl p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Sistema de Logros</h2>
              <p className="text-white/60 text-sm">Gestión y configuración</p>
            </div>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Logro
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-black/20 rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-purple-400 mb-1">
              <Trophy className="w-4 h-4" />
              <span className="font-bold text-lg">{totalAchievementsCount}</span>
            </div>
            <p className="text-xs text-white/50">Logros Activos</p>
          </div>
          <div className="bg-black/20 rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-amber-400 mb-1">
              <Sparkles className="w-4 h-4" />
              <span className="font-bold text-lg">{totalPoints}</span>
            </div>
            <p className="text-xs text-white/50">Puntos Totales</p>
          </div>
          <div className="bg-black/20 rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-green-400 mb-1">
              <Users className="w-4 h-4" />
              {statsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span className="font-bold text-lg">{systemStats.totalSellersWithAchievements}</span>
              )}
            </div>
            <p className="text-xs text-white/50">Con Logros</p>
          </div>
        </div>
      </motion.div>

      {/* System Achievements (Editable) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wide">
            Logros del Sistema
          </h3>
          <span className="text-xs text-white/40 bg-white/5 px-2 py-0.5 rounded-full">
            {allMergedAchievements.length} logros • {totalSystemPoints} pts
          </span>
          <span className="text-xs text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded-full ml-2">
            <Edit2 className="w-3 h-3 inline mr-1" />
            Editables
          </span>
        </div>

        <TooltipProvider delayDuration={100}>
          {Object.entries(groupedSystemAchievements).map(([category, achievements]) => {
            if (achievements.length === 0) return null;
            const catInfo = CATEGORY_INFO[category];
            const categoryPoints = achievements.filter(a => a.isActive).reduce((sum, a) => sum + a.points, 0);
            
            return (
              <div key={category} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{catInfo.icon}</span>
                  <span className="text-sm font-medium text-white/80">{catInfo.name}</span>
                  <span className="text-xs text-amber-400/80 ml-auto">{categoryPoints} pts</span>
                  <span className="text-xs text-white/40">• {achievements.length} logros</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {achievements.map((achievement) => (
                    <Tooltip key={achievement.type}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleEditSystemAchievement(achievement)}
                          className={`relative w-11 h-11 rounded-lg flex items-center justify-center text-xl cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50 group ${
                            achievement.isActive
                              ? 'bg-zinc-800 hover:bg-zinc-700 hover:scale-110 hover:shadow-lg hover:shadow-purple-500/20'
                              : 'bg-zinc-900 opacity-50 hover:opacity-75'
                          }`}
                        >
                          {achievement.badge}
                          {/* Edit indicator */}
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Edit2 className="w-2.5 h-2.5 text-white" />
                          </div>
                          {/* Modified indicator */}
                          {achievement.isOverridden && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-500" />
                          )}
                          {/* Inactive indicator */}
                          {!achievement.isActive && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                              <ToggleLeft className="w-4 h-4 text-zinc-500" />
                            </div>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="top" 
                        className="max-w-xs bg-zinc-900 border-zinc-700 p-0 overflow-hidden"
                        sideOffset={8}
                      >
                        <div className="p-3 space-y-2">
                          {/* Header */}
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center text-2xl">
                              {achievement.badge}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-white text-sm">{achievement.name}</h4>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs font-medium text-amber-400">+{achievement.points} pts</span>
                                {achievement.isOverridden && (
                                  <span className="text-xs text-purple-400">(modificado)</span>
                                )}
                                <span className="text-xs text-white/40">
                                  {achievement.isPermanent ? (
                                    <span className="flex items-center gap-1">
                                      <Lock className="w-3 h-3" /> Permanente
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <RefreshCw className="w-3 h-3" /> Ciclo
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Description */}
                          <p className="text-xs text-white/70 leading-relaxed">
                            {achievement.description}
                          </p>
                          
                          {/* Requirement */}
                          <div className="pt-2 border-t border-zinc-700/50">
                            <p className="text-xs text-white/50">
                              <span className="font-medium text-white/70">Requisito:</span> {achievement.requirement}
                            </p>
                          </div>
                          
                          {/* Click to edit hint */}
                          <div className="pt-2 border-t border-zinc-700/50">
                            <p className="text-xs text-purple-400 text-center">
                              Click para editar
                            </p>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </div>
            );
          })}
        </TooltipProvider>
      </motion.div>

      {/* Custom Achievements (Editable) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wide">
              Logros Personalizados
            </h3>
            <span className="text-xs text-white/40 bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
              {customAchievements.length} creados
            </span>
          </div>
        </div>

        {customLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
          </div>
        ) : customAchievements.length === 0 ? (
          <div className="bg-zinc-900/50 border border-dashed border-zinc-700 rounded-xl p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-3">
              <Trophy className="w-6 h-6 text-zinc-600" />
            </div>
            <p className="text-white/60 text-sm">No hay logros personalizados</p>
            <p className="text-white/40 text-xs mt-1">Crea logros únicos para motivar a tu equipo</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(true)}
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Primer Logro
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {customAchievements.map((achievement) => (
              <motion.div
                key={achievement.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`bg-zinc-900/50 border rounded-xl p-4 transition-all ${
                  achievement.is_active
                    ? 'border-purple-500/30 bg-gradient-to-r from-purple-500/5 to-pink-500/5'
                    : 'border-zinc-800 opacity-60'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                    achievement.is_active
                      ? 'bg-gradient-to-br from-purple-500 to-pink-600 shadow-lg'
                      : 'bg-zinc-800'
                  }`}>
                    {achievement.badge}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-white truncate">{achievement.name}</h4>
                      {!achievement.is_active && (
                        <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">
                          Inactivo
                        </span>
                      )}
                      {achievement.is_permanent && (
                        <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">
                          Permanente
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white/60 truncate">{achievement.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-purple-400">+{achievement.points} pts</span>
                      <span className="text-xs text-white/40">
                        {CATEGORY_INFO[achievement.category]?.icon} {CATEGORY_INFO[achievement.category]?.name || achievement.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(achievement.id, achievement.is_active)}
                      className="h-8 w-8"
                      title={achievement.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {achievement.is_active ? (
                        <ToggleRight className="w-4 h-4 text-green-400" />
                      ) : (
                        <ToggleLeft className="w-4 h-4 text-zinc-500" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(achievement)}
                      className="h-8 w-8"
                    >
                      <Edit2 className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingId(achievement.id)}
                      className="h-8 w-8 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Info card */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3"
      >
        <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-blue-300 font-medium">Sobre los tipos de logros</p>
          <ul className="text-blue-400/80 mt-1 space-y-1">
            <li>• <strong>Logros del sistema:</strong> Se asignan automáticamente según métricas. Puedes editar puntos y descripciones.</li>
            <li>• <strong>Logros personalizados:</strong> Los asignas manualmente desde el perfil del vendedor.</li>
            <li>• <strong>Indicador naranja:</strong> El logro ha sido modificado. Puedes restaurar valores por defecto.</li>
          </ul>
        </div>
      </motion.div>

      {/* Custom Achievement Form Modal */}
      <AnimatePresence>
        {showForm && (
          <AchievementForm
            achievement={editingAchievement || undefined}
            onSubmit={handleFormSubmit}
            onClose={handleCloseForm}
          />
        )}
      </AnimatePresence>

      {/* System Achievement Edit Dialog */}
      <Dialog open={!!editingSystemAchievement} onOpenChange={(open) => !open && setEditingSystemAchievement(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="text-2xl">{systemEditForm.badge}</span>
              <span className="text-white">Editar Logro del Sistema</span>
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modifica los valores de este logro. Los cambios se aplicarán inmediatamente al ranking.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[65vh] overflow-y-auto">
            {/* Badge */}
            <div className="space-y-2">
              <Label className="text-white/80">Emoji / Badge</Label>
              <Input
                value={systemEditForm.badge}
                onChange={(e) => setSystemEditForm(prev => ({ ...prev, badge: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white text-center text-2xl"
                maxLength={4}
              />
            </div>
            
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-white/80">Nombre</Label>
              <Input
                value={systemEditForm.name}
                onChange={(e) => setSystemEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
            
            {/* Points */}
            <div className="space-y-2">
              <Label className="text-white/80">Puntos</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={systemEditForm.points}
                  onChange={(e) => setSystemEditForm(prev => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
                  className="bg-zinc-800 border-zinc-700 text-white w-24"
                  min={0}
                  max={100}
                />
                <span className="text-zinc-400 text-sm">pts</span>
                {editingSystemAchievement?.isOverridden && editingSystemAchievement.originalPoints !== systemEditForm.points && (
                  <span className="text-xs text-amber-400">
                    (original: {editingSystemAchievement.originalPoints} pts)
                  </span>
                )}
              </div>
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label className="text-white/80">Descripción</Label>
              <Textarea
                value={systemEditForm.description}
                onChange={(e) => setSystemEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 resize-none min-h-0"
                rows={1}
              />
            </div>
            
            {/* Requirement */}
            <div className="space-y-2">
              <Label className="text-white/80">Requisito</Label>
              <Textarea
                value={systemEditForm.requirement}
                onChange={(e) => setSystemEditForm(prev => ({ ...prev, requirement: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 resize-none min-h-0"
                rows={1}
              />
            </div>
            
            {/* Active toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-white/80">Logro activo</Label>
                <p className="text-xs text-zinc-500">Los logros inactivos no suman puntos al ranking</p>
              </div>
              <Switch
                checked={systemEditForm.is_active}
                onCheckedChange={(checked) => setSystemEditForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center pt-4 border-t border-zinc-800 sticky bottom-0 bg-zinc-900">
            {/* Action buttons row */}
            <div className="flex flex-wrap gap-2">
              {/* Deactivate - only show if active */}
              {systemEditForm.is_active && (
                <Button
                  variant="outline"
                  onClick={handleDeactivateSystemAchievement}
                  disabled={savingSystemEdit}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  {savingSystemEdit ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Ban className="w-4 h-4 mr-2" />
                  )}
                  Desactivar
                </Button>
              )}
              
              {/* Activate - only show if inactive */}
              {!systemEditForm.is_active && (
                <Button
                  variant="outline"
                  onClick={handleActivateSystemAchievement}
                  disabled={savingSystemEdit}
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                >
                  {savingSystemEdit ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Activar
                </Button>
              )}
              
              {/* Restore default */}
              {editingSystemAchievement?.isOverridden && (
                <Button
                  variant="outline"
                  onClick={() => editingSystemAchievement && handleRestoreDefault(editingSystemAchievement.type)}
                  disabled={restoringDefault === editingSystemAchievement?.type || savingSystemEdit}
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                >
                  {restoringDefault === editingSystemAchievement?.type ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4 mr-2" />
                  )}
                  Restaurar
                </Button>
              )}
            </div>
            
            {/* Main action buttons */}
            <div className="flex gap-2 sm:ml-auto">
              <Button
                variant="outline"
                onClick={() => setEditingSystemAchievement(null)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveSystemAchievement}
                disabled={savingSystemEdit}
                className="bg-purple-500 hover:bg-purple-600"
              >
                {savingSystemEdit ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Guardar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar este logro?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta acción no se puede deshacer. El logro será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
